import {
  VALUATIONS_PERCENTILE_PATTERN as patterns,
  SELECTION_A,
  SELECTION_B,
  VALUATIONS_OTHER_LABEL,
} from 'common/constants/valuations';
import {
  PERCENTILE_25,
  PERCENTILE_75,
  SELECTED_DATABASE_VALUE,
  SPECIFIED_LABEL,
  VALUES_OPTIONS,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/constants';
import { BenchmarkApproach, matchPattern } from 'pages/ValuationsAllocation/util';
import { SEVENTY_FIVE_STRING, TWENTY_FIVE_STRING } from './constants';

type DetermineDatabaseOptionParams = {
  options: string[];
  value: string;
  specificApproach: BenchmarkApproach;
};

export const determineDatabaseOption = ({ options, value, specificApproach }: DetermineDatabaseOptionParams) => {
  // handle different name between frontend label and database value
  const selection = parseInt(value, 10);
  // index of the penultimate option, which is the specified value
  const penultimateOption = options.length - 2;
  const isSpecifiedValue = selection > VALUES_OPTIONS.length || selection === penultimateOption;

  if (Number.isNaN(selection)) return null;

  if (options.length === VALUES_OPTIONS.length || [0, 1, penultimateOption].includes(selection)) {
    return isSpecifiedValue ? SELECTED_DATABASE_VALUE : VALUES_OPTIONS[selection];
  }

  if (selection === options.length - 1) return SELECTED_DATABASE_VALUE;
  // order and amount in frontend != order and amount in backend options
  const currentSelectionA = specificApproach.percentile_selection_a;
  const currentSelectionB = specificApproach.percentile_selection_b;
  const indexedOption = options[selection];
  const matchedPattern = matchPattern({
    label: indexedOption,
    patterns,
  });
  const pickedPercentile = indexedOption.split(matchedPattern)[0];
  const optionsMap = new Map([
    [currentSelectionA?.toString(), SELECTION_A],
    [currentSelectionB?.toString(), SELECTION_B],
    [TWENTY_FIVE_STRING, PERCENTILE_25],
    [SEVENTY_FIVE_STRING, PERCENTILE_75],
  ]);
  return optionsMap.get(pickedPercentile);
};

const getAdHocOptionLabel = (option: string, specificApproach: BenchmarkApproach) => {
  if (option === SELECTED_DATABASE_VALUE) return SPECIFIED_LABEL;
  const validFixedoptions = [...VALUES_OPTIONS, VALUATIONS_OTHER_LABEL];
  if (validFixedoptions.includes(option)) return option;
  const availablePercentiles = {
    [specificApproach.percentile_selection_a as number]: SELECTION_A,
    [specificApproach.percentile_selection_b as number]: SELECTION_B,
  };
  const matchedPattern = matchPattern({
    label: option,
    patterns,
  });
  if (matchedPattern) return availablePercentiles[parseInt(option.split(matchedPattern.toString())[0], 10)];
};

type MapVariableLabelsToSelectionIndexParams = {
  options: string[];
  selection: string;
  specificApproach: BenchmarkApproach;
};

export const mapVariableLabelsToSelectionIndex = ({
  options,
  selection,
  specificApproach,
}: MapVariableLabelsToSelectionIndexParams) => {
  // if there isn't a benchmark approach, we only display 'Other' as choice => index = 0
  if (options.length === 1) return 0;

  const labelIndexPair: [string, number][] = options.map((option, index) => {
    const adHocLabel = getAdHocOptionLabel(option, specificApproach);
    return [adHocLabel ?? '', index];
  });
  const optionsMap = new Map(labelIndexPair);
  const selected = selection === SELECTED_DATABASE_VALUE ? SPECIFIED_LABEL : selection;
  return optionsMap.get(selected) ?? null;
};
